import { baseApi } from '@/store/baseApi';
import { CacheDurations } from '@web/utils';

import type {
  BulkDeleteCompaniesPayload,
  BulkDeleteCompaniesResponse,
  CreateCompanyPayload,
  CreateCompanyResponse,
  DeleteCompanyResponse,
  GetCompaniesParams,
  GetCompanyResponse,
  UpdateCompanyPayload,
  UpdateCompanyResponse,
} from '@/features/contacts/companies/types';
import type { Company } from '@/features/contacts/types';
import type { APIResponse, PaginatedData } from '@web/types';

export const createCompanyInvalidatesTags = () => [
  { type: 'People' as const, id: 'LIST' },
  { type: 'Companies' as const, id: 'LIST' },
];

export const deleteCompanyInvalidatesTags = (
  _result: unknown,
  _error: unknown,
  companyId: string,
) => [
  { type: 'Companies' as const, id: companyId },
  { type: 'Companies' as const, id: 'LIST' },
  { type: 'People' as const, id: 'LIST' },
];

export const updateCompanyInvalidatesTags = (
  _result: unknown,
  _error: unknown,
  companyId: string,
) => [
  { type: 'Companies' as const, id: companyId },
  { type: 'Companies' as const, id: 'LIST' },
  { type: 'People' as const, id: 'LIST' },
];

export const bulkDeleteCompaniesInvalidatesTags = () => [
  { type: 'Companies' as const, id: 'LIST' },
  { type: 'People' as const, id: 'LIST' },
];

type UpdateCompanyArgs = {
  companyId: string;
  payload: UpdateCompanyPayload;
};

export const companiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<
      APIResponse<PaginatedData<Company>>,
      GetCompaniesParams | void
    >({
      query: (params) => ({
        url: 'contacts/companies',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.MEDIUM,
      providesTags: (result) =>
        result?.data?.results
          ? [
              ...result.data.results.map((company) => ({
                type: 'Companies' as const,
                id: company.id,
              })),
              { type: 'Companies' as const, id: 'LIST' },
            ]
          : [{ type: 'Companies' as const, id: 'LIST' }],
    }),
    getCompany: builder.query<APIResponse<GetCompanyResponse>, string>({
      query: (companyId) => ({
        url: `contacts/companies/${companyId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: (_result, _error, id) => [{ type: 'Companies', id }],
    }),
    createCompany: builder.mutation<
      APIResponse<CreateCompanyResponse>,
      CreateCompanyPayload
    >({
      query: (body) => ({
        url: 'contacts/companies',
        method: 'POST',
        body,
      }),
      invalidatesTags: createCompanyInvalidatesTags,
    }),
    updateCompany: builder.mutation<
      APIResponse<UpdateCompanyResponse>,
      UpdateCompanyArgs
    >({
      query: ({ companyId, payload }) => ({
        url: `contacts/companies/${companyId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, args) =>
        updateCompanyInvalidatesTags(_result, _error, args.companyId),
    }),
    deleteCompany: builder.mutation<APIResponse<DeleteCompanyResponse>, string>(
      {
        query: (companyId) => ({
          url: `contacts/companies/${companyId}`,
          method: 'DELETE',
        }),
        invalidatesTags: deleteCompanyInvalidatesTags,
      },
    ),
    bulkDeleteCompanies: builder.mutation<
      APIResponse<BulkDeleteCompaniesResponse>,
      BulkDeleteCompaniesPayload
    >({
      query: (body) => ({
        url: 'contacts/companies/bulk-delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: bulkDeleteCompaniesInvalidatesTags,
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useLazyGetCompaniesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useGetCompanyQuery,
  useDeleteCompanyMutation,
  useBulkDeleteCompaniesMutation,
} = companiesApi;
